import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { NSCA_ROWS, RPE_GRID, RPE_REPS, roundTo, toNum, rpeForPct, fmt } from "./calc";

const RED = [205, 56, 61];
const BLACK = [12, 14, 20];

export const LOGO_URL = `${process.env.PUBLIC_URL || ""}/kraken-logo.png`;

let logoCache = null;

// Convertit le logo (webp) en PNG base64 utilisable par jsPDF
const loadLogo = () => {
  if (logoCache) return logoCache;
  logoCache = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const MAX = 600;
        const scale = Math.min(1, MAX / Math.max(img.naturalWidth, img.naturalHeight));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.naturalWidth * scale);
        canvas.height = Math.round(img.naturalHeight * scale);
        canvas
          .getContext("2d")
          .drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({
          data: canvas.toDataURL("image/png"),
          ratio: img.naturalWidth / img.naturalHeight,
        });
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = LOGO_URL;
  });
  return logoCache;
};

const drawHeader = (doc, subtitle, logo) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(...BLACK);
  doc.rect(0, 0, pageWidth, 26, "F");
  doc.setFillColor(...RED);
  doc.rect(0, 26, pageWidth, 1.2, "F");

  let x = 12;
  if (logo) {
    const h = 16;
    const w = h * logo.ratio;
    doc.addImage(logo.data, "PNG", x, 5, w, h, "kplogo", "FAST");
    x += w + 5;
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("KRAKEN", x, 15);
  const kw = doc.getTextWidth("KRAKEN");
  doc.setTextColor(...RED);
  doc.text("PP", x + kw + 2, 15);
  doc.setTextColor(190, 190, 195);
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "normal");
  doc.text("Préparation physique · krakenpp.ca", x, 20.5);

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(subtitle, pageWidth - 12, 15, { align: "right" });
};

// Filigrane discret au centre de la page
const drawWatermark = (doc, logo) => {
  if (!logo) return;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const w = pageWidth * 0.55;
  const h = w / logo.ratio;
  const gs = doc.GState ? new doc.GState({ opacity: 0.05 }) : null;
  if (gs) doc.setGState(gs);
  doc.addImage(
    logo.data,
    "PNG",
    (pageWidth - w) / 2,
    (pageHeight - h) / 2,
    w,
    h,
    "kplogo",
    "FAST",
  );
  if (gs) doc.setGState(new doc.GState({ opacity: 1 }));
};

const drawFooter = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setDrawColor(220);
  doc.line(12, pageHeight - 14, pageWidth - 12, pageHeight - 14);
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.setFont("helvetica", "normal");
  doc.text(
    `Généré le ${new Date().toLocaleDateString("fr-CA")} · krakenpp.ca · RPE/RIR : barème RTS (Tuchscherer)`,
    12,
    pageHeight - 8,
  );
};

const chartRows = (rm, unit, rounding) =>
  NSCA_ROWS.map((r) => {
    const rpe = rpeForPct(r.pct);
    return [
      `${r.pct}%`,
      `${r.reps} RM`,
      `${rpe.rpe} (${rpe.rir} RIR)`,
      `${fmt(roundTo((rm * r.pct) / 100, rounding), rounding ? 0 : 1)} ${unit}`,
    ];
  });

const chartTable = (doc, rm, unit, rounding, startY) => {
  autoTable(doc, {
    head: [["% 1RM", "Reps max", "RPE / RIR", `Charge (${unit})`]],
    body: chartRows(rm, unit, rounding),
    startY,
    theme: "grid",
    styles: { font: "helvetica", fontSize: 9.5, cellPadding: 2.6, textColor: BLACK },
    headStyles: {
      fillColor: BLACK,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "left",
    },
    columnStyles: {
      0: { fontStyle: "bold", textColor: RED, cellWidth: 20 },
      1: { cellWidth: 22 },
      2: { cellWidth: 40, textColor: [80, 80, 90] },
      3: { halign: "right", fontStyle: "bold", cellWidth: 40 },
    },
    alternateRowStyles: { fillColor: [246, 246, 248] },
    margin: { left: 12, right: 12 },
  });
  return doc.lastAutoTable.finalY;
};

const rpeGridTable = (doc, startY) => {
  autoTable(doc, {
    head: [["RPE / RIR", ...RPE_REPS.map((r) => `${r}`)]],
    body: RPE_GRID.map((r) => [
      `${r.rpe} · ${r.rir}`,
      ...r.pcts.map((p) => `${p}`),
    ]),
    startY,
    theme: "grid",
    styles: { font: "helvetica", fontSize: 7.5, cellPadding: 1.6, halign: "center" },
    headStyles: { fillColor: BLACK, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: { 0: { fontStyle: "bold", textColor: RED, halign: "left", cellWidth: 24 } },
    alternateRowStyles: { fillColor: [246, 246, 248] },
    margin: { left: 12, right: 12 },
  });
  return doc.lastAutoTable.finalY;
};

const sectionTitle = (doc, text, y) => {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...BLACK);
  doc.text(text.toUpperCase(), 12, y);
  doc.setDrawColor(...RED);
  doc.setLineWidth(0.6);
  doc.line(12, y + 1.6, 40, y + 1.6);
  doc.setLineWidth(0.2);
};

export const exportChartPDF = async ({ oneRM, unit, rounding, exercise }) => {
  const rm = toNum(oneRM);
  const logo = await loadLogo();
  const doc = new jsPDF({ unit: "mm", format: "letter", compress: true });

  drawWatermark(doc, logo);
  drawHeader(doc, "Fiche de charges", logo);

  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BLACK);
  doc.text(exercise.toUpperCase(), 12, 42);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(110);
  doc.text("Charge maximale (1RM)", 12, 49);
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...RED);
  doc.text(`${fmt(rm, 1)} ${unit}`, 12, 58);

  let y = chartTable(doc, rm, unit, rounding, 64);
  drawFooter(doc);

  doc.addPage();
  drawWatermark(doc, logo);
  drawHeader(doc, "Grille Reps @ RPE", logo);
  sectionTitle(doc, "Barème RTS — % du 1RM selon reps et RPE", 40);
  rpeGridTable(doc, 45);
  doc.setFontSize(8);
  doc.setTextColor(110);
  doc.setFont("helvetica", "normal");
  doc.text(
    "RPE 10 = 0 rep en réserve · 9 = 1 RIR · 8 = 2 RIR · 7 = 3 RIR · 6 = 4 RIR",
    12,
    doc.lastAutoTable.finalY + 6,
  );
  drawFooter(doc);

  doc.save(`kraken-pp_charges_${exercise.replace(/\s+/g, "-")}.pdf`);
};

export const exportCoachPDF = async ({ athletes, exercises, unit, rounding }) => {
  const logo = await loadLogo();
  const doc = new jsPDF({ unit: "mm", format: "letter", orientation: "landscape", compress: true });

  drawWatermark(doc, logo);
  drawHeader(doc, "Feuille d'équipe · Multi-athlètes", logo);

  doc.setFontSize(9);
  doc.setTextColor(90);
  doc.setFont("helvetica", "normal");
  doc.text(
    `Unité : ${unit.toUpperCase()} · Arrondi : ${rounding || "exact"}`,
    12,
    36,
  );

  autoTable(doc, {
    head: [["Athlète", "Catégorie", ...exercises.map((e) => `${e} (${unit})`)]],
    body: athletes.map((a) => [
      a.name,
      a.category || "—",
      ...exercises.map((e) => {
        const v = toNum(a.records?.[e]);
        return v ? `${roundTo(v, rounding)}` : "—";
      }),
    ]),
    startY: 42,
    theme: "grid",
    styles: { font: "helvetica", fontSize: 9, cellPadding: 2.5, textColor: BLACK },
    headStyles: { fillColor: BLACK, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 44 },
      1: { textColor: [110, 110, 120], cellWidth: 40 },
    },
    alternateRowStyles: { fillColor: [246, 246, 248] },
    margin: { left: 12, right: 12 },
  });

  const y = doc.lastAutoTable.finalY + 8;
  sectionTitle(doc, "Repères RPE / RIR — RTS (Tuchscherer)", y);
  autoTable(doc, {
    head: [["RPE", "Reps en réserve", "Intensité indicative"]],
    body: [
      ["10", "0", "100% · 1RM"],
      ["9", "1", "93–95%"],
      ["8", "2", "85–90%"],
      ["7", "3", "80–85%"],
      ["6", "4+", "< 80% · technique"],
    ],
    startY: y + 5,
    theme: "grid",
    styles: { font: "helvetica", fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: BLACK, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: { 0: { fontStyle: "bold", textColor: RED, cellWidth: 20 } },
    margin: { left: 12, right: 12 },
    tableWidth: 140,
  });

  drawFooter(doc);
  doc.save(`kraken-pp_equipe_${new Date().toISOString().slice(0, 10)}.pdf`);
};

// Fiche complète d'un athlète : tous les exercices + grille RPE
export const exportAthleteChartPDF = async ({
  athlete,
  exercises,
  exercise,
  unit,
  rounding,
}) => {
  const logo = await loadLogo();
  const doc = new jsPDF({ unit: "mm", format: "letter", compress: true });
  const list = (exercises && exercises.length ? exercises : [exercise]).filter(
    (e) => toNum(athlete.records?.[e]) > 0,
  );
  const all = list.length ? list : [exercise];

  drawWatermark(doc, logo);
  drawHeader(doc, "Fiche athlète", logo);

  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BLACK);
  doc.text((athlete.name || "Athlète").toUpperCase(), 12, 42);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(120);
  doc.text(
    `${athlete.category || ""} · Unité ${unit.toUpperCase()} · Arrondi ${rounding || "exact"}`,
    12,
    48,
  );

  // Récapitulatif des 1RM
  sectionTitle(doc, "Maximums (1RM)", 58);
  autoTable(doc, {
    head: [["Exercice", `1RM (${unit})`]],
    body: all.map((e) => [e, `${fmt(roundTo(toNum(athlete.records?.[e]), rounding), 1)}`]),
    startY: 62,
    theme: "grid",
    styles: { font: "helvetica", fontSize: 9.5, cellPadding: 2.4 },
    headStyles: { fillColor: BLACK, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 70 },
      1: { halign: "right", textColor: RED, fontStyle: "bold", cellWidth: 34 },
    },
    alternateRowStyles: { fillColor: [246, 246, 248] },
    margin: { left: 12, right: 12 },
    tableWidth: 104,
  });
  drawFooter(doc);

  // Une page de charges par exercice
  all.forEach((e) => {
    const rm = toNum(athlete.records?.[e]);
    doc.addPage();
    drawWatermark(doc, logo);
    drawHeader(doc, "Fiche athlète", logo);
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120);
    doc.text((athlete.name || "").toUpperCase(), 12, 38);
    doc.setFontSize(19);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...BLACK);
    doc.text(e.toUpperCase(), 12, 47);
    doc.setFontSize(18);
    doc.setTextColor(...RED);
    doc.text(`${fmt(rm, 1)} ${unit}`, 12, 57);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120);
    doc.text("1RM prescrit", 12, 62);
    chartTable(doc, rm, unit, rounding, 68);
    drawFooter(doc);
  });

  // Grille RPE
  doc.addPage();
  drawWatermark(doc, logo);
  drawHeader(doc, "Grille Reps @ RPE", logo);
  sectionTitle(doc, "Barème RTS — % du 1RM selon reps et RPE", 40);
  rpeGridTable(doc, 45);
  doc.setFontSize(8);
  doc.setTextColor(110);
  doc.setFont("helvetica", "normal");
  doc.text(
    "RPE 10 = 0 rep en réserve · 9 = 1 RIR · 8 = 2 RIR · 7 = 3 RIR · 6 = 4 RIR",
    12,
    doc.lastAutoTable.finalY + 6,
  );
  drawFooter(doc);

  doc.save(`kraken-pp_${(athlete.name || "athlete").replace(/\s+/g, "-")}.pdf`);
};
